import { useState, type FormEvent, type ReactNode } from 'react';
import { BORNES_BULLE } from '@nextilt/commun';
import { Modele } from '../modeles';
import './Premier.css';

/**
 * Ouvrir son premier dossier : le champ et le raccourci du modèle, les mêmes sur les deux
 * pages vides (ticket 14 de retours-testeurs) : « Maintenant », qui accueille avec « Par quoi
 * tu commences ? » (`Premier.tsx`), et « Projets », qui présente « Maintenant »
 * (`ProjetsVides.tsx`).
 *
 * Le champ n'est pas une boîte mais **une ligne soulignée** : rien à remplir, une chose à
 * écrire. C'est la même grammaire que la ligne du mot de passe. Des idées en dessous, pour
 * débloquer celui qui ne sait pas par où commencer.
 */
export function OuvrirUnDossier({
  libelle,
  exemple,
  idees,
  onOuvrir,
  focus = false,
  refus = null,
  children,
}: {
  /** Pourquoi le dossier n'a pas pu s'ouvrir (sans réseau) ; le nom reste dans le champ. */
  refus?: string | null;
  libelle: string;
  /** Le nom écrit en pâle dans le champ vide. */
  exemple: string;
  idees: readonly string[];
  onOuvrir: (titre: string) => void;
  /** Le curseur dans le champ à l'arrivée : seulement quand le champ est en haut de la page. */
  focus?: boolean;
  children?: ReactNode;
}) {
  const [nom, setNom] = useState('');

  function ouvrir(evenement: FormEvent) {
    evenement.preventDefault();
    const propre = nom.trim();
    if (propre) onOuvrir(propre);
  }

  return (
    <form className="premier-carte" data-tuto="premier-carte" onSubmit={ouvrir}>
      <label htmlFor="nouveau-dossier">{libelle}</label>

      <div className="premier-saisie">
        <input
          id="nouveau-dossier"
          data-tuto="champ-nouveau-dossier"
          value={nom}
          onChange={(e) => setNom(e.target.value)}
          placeholder={exemple}
          maxLength={BORNES_BULLE.titreMax}
          autoFocus={focus}
        />
        <button className="bouton premier-ouvrir" type="submit" disabled={!nom.trim()}>
          Ouvrir
        </button>
      </div>

      {refus && (
        <p className="refus refus-sous-le-champ" role="status">
          {refus}
        </p>
      )}

      <div className="premier-exemples">
        <span className="premier-idees">Ou, si tu manques d’idées&nbsp;:</span>
        {idees.map((idee) => (
          <button key={idee} type="button" onClick={() => setNom(idee)}>
            {idee}
          </button>
        ))}
      </div>

      {children}
    </form>
  );
}

/** Un raccourci proposé, jamais imposé : le pointillé dit « une place à prendre ». */
export function PartirDUnModele() {
  const [ouvert, setOuvert] = useState(false);
  return (
    <>
      <section className="premier-modele">
        <span className="premier-modele-barre" aria-hidden="true" />
        <div className="premier-modele-dit">
          <h2>Ou pars d’un modèle</h2>
          <p>
            Un modèle apporte la liste des démarches et les délais de
            l’administration. Il ne t’apportera jamais l’ordre dans lequel <em>toi</em> tu t’y
            prends.
          </p>
        </div>
        <button type="button" onClick={() => setOuvert(true)}>
          Voir le modèle
        </button>
      </section>
      {ouvert && <Modele onFermer={() => setOuvert(false)} />}
    </>
  );
}
